import { useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Toast, ToastMessage } from 'primereact/toast'
import { Icon } from '@iconify/react'
import {
  InputPhone,
  InputTextCustom,
  Modal,
  initialStatePhoneNumber,
  mobilePhoneToString,
  validMobileForInputPhone,
} from 'medere-ui-framework'
import PdfViewer from '../../../Recipe/Components/PdfViewer'
import {
  getStudyInformPdfFile,
  getStudyById,
  sendStudyInformByMessageSender,
} from '../../../../services/StudiesInformController'
import { StudiesInformDetailsDTO } from '../../../../dto/StudiesInformListItemDTO'
import { EMessageType } from '../../enums'
import { ETaskMessageType } from '../../../Recipe/RecipeEnums'
import { hasSenderPermissionByTaskMessageTypeAndMessageType } from '../../../../servicesHooks/DigitalRP/useDigitalRPService'
import { isValidEmail, validateInternationalCellphone } from '../../../../utils/utils'
import { customerServiceNumber, whatsappPurchaseText } from '../../../../utils/Constants';

interface StudyInformPdfViewProps {
  studyDiagnosticId: number
  onClose: () => void
}

export default function StudyInformPdfView({ studyDiagnosticId, onClose }: StudyInformPdfViewProps) {
  const toast = useRef(null)

  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [pdfFileId, setPdfFileId] = useState<number | null>(null)
  const [details, setDetails] = useState<StudiesInformDetailsDTO | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [disabledWhatsapp, setDisabledWhatsapp] = useState(true)
  const [disabledEmail, setDisabledEmail] = useState(true)
  const [loadingPerms, setLoadingPerms] = useState(true)
  const [viaSended, setViaSended] = useState<number | null>(null)
  const [sendedModal, setSendedModal] = useState(false)

  const [openEditMobilephoneModal, setOpenEditMobilephoneModal] = useState(false)
  const [patientPhone, setPatientPhone] = useState(initialStatePhoneNumber)
  const [newMobilephone, setNewMobilephone] = useState('')

  const [openEditMailModal, setOpenEditMailModal] = useState(false)
  const [newMail, setNewMail] = useState('')

  const showMessage = (ref: React.RefObject<Toast>, msg: string, severity: ToastMessage['severity']) => {
    ref.current?.show({ severity, summary: msg, life: 3000 })
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    Promise.all([getStudyInformPdfFile(studyDiagnosticId), getStudyById(studyDiagnosticId)])
      .then(([file, det]) => {
        setPdfBase64(file.encodedPdf)
        setPdfFileId(file.id ?? null)
        setDetails(det)
      })
      .catch(() => setError('No se pudo generar el PDF. Intente nuevamente.'))
      .finally(() => setLoading(false))
  }, [studyDiagnosticId])

  useEffect(() => {
    const fetchPermission = async () => {
      try {
        const waDto = await hasSenderPermissionByTaskMessageTypeAndMessageType(
          EMessageType.WHATSAPP,
          ETaskMessageType.STUDY_RESULT,
        )
        setDisabledWhatsapp(!waDto?.hassenderpermission)

        const emailDto = await hasSenderPermissionByTaskMessageTypeAndMessageType(
          EMessageType.EMAIL,
          ETaskMessageType.STUDY_RESULT,
        )
        setDisabledEmail(!emailDto?.hassenderpermission)
      } catch {
        setDisabledWhatsapp(true)
        setDisabledEmail(true)
      } finally {
        setLoadingPerms(false)
      }
    }
    fetchPermission()
  }, [])

  useEffect(() => {
    if (!openEditMobilephoneModal) setNewMobilephone('')
  }, [openEditMobilephoneModal])

  useEffect(() => {
    if (!openEditMailModal) setNewMail('')
  }, [openEditMailModal])

  const phone = details?.phonenumber ?? null
  const email = details?.email ?? null
  const canSend = !!pdfFileId && !sending

  const doSend = async (messageType: number) => {
    if (!canSend) return

    if (messageType === EMessageType.WHATSAPP && disabledWhatsapp) {
      showMessage(toast, 'La institución no tiene contratado el envío por WhatsApp.', 'warn')
      return
    }
    if (messageType === EMessageType.EMAIL && disabledEmail) {
      showMessage(toast, 'La institución no tiene habilitado el envío por Email.', 'warn')
      return
    }

    if (messageType === EMessageType.WHATSAPP) {
      const validation = validateInternationalCellphone(phone)
      if (!validation.isValid()) {
        showMessage(toast, validation.errorMessage!, 'error')
        return
      }
    }

    try {
      setSending(true)
      await sendStudyInformByMessageSender(
        studyDiagnosticId,
        messageType,
        messageType === EMessageType.WHATSAPP ? phone : null,
        messageType === EMessageType.EMAIL ? email : null,
      )
      setViaSended(messageType)
      setSendedModal(true)
    } catch {
      showMessage(toast, 'Ocurrió un error en el envío', 'error')
    } finally {
      setSending(false)
    }
  }

  const handleOpenEditMobilephoneModal = () => {
    if (details?.phonenumber) {
      setPatientPhone(validMobileForInputPhone(details.phonenumber))
      setNewMobilephone(details.phonenumber)
    } else {
      setPatientPhone(initialStatePhoneNumber)
      setNewMobilephone('')
    }
    setOpenEditMobilephoneModal(true)
  }

  const handleOpenEditMailModal = () => {
    if (details?.email) setNewMail(details.email)
    setOpenEditMailModal(true)
  }

  function handleShare() {
    if (!pdfBase64) return
    const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))
    const file = new File([bytes], 'informe.pdf', { type: 'application/pdf' })
    if (navigator.canShare?.({ files: [file] })) {
      navigator.share({ files: [file], title: 'Informe de estudio' })
    } else {
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      window.open(url, '_blank')
    }
  }

  const handleDownload = () => {
    if (!pdfBase64) return
    const a = document.createElement('a')
    a.href = `data:application/pdf;base64,${pdfBase64}`
    a.download = 'informe.pdf'
    a.click()
  }

  return (
    <div className='viewContainer studies-inform-view'>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem' }}>
        <span style={{ fontWeight: 600, fontSize: '1.15rem', color: '#B27668' }}>Informe</span>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <i className='pi pi-spin pi-spinner' style={{ fontSize: '2rem', color: '#3CB8BA' }} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#B27668' }}>{error}</div>
      )}

      {!loading && !error && pdfBase64 && (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1.5rem', padding: '0 1rem 1rem', overflowX: 'auto' }}>
          <div style={{ flex: '1', minWidth: 0 }}>
            <PdfViewer file={pdfBase64} />
          </div>

          <div style={{
            flex: '0 0 360px',
            minWidth: '280px',
            borderLeft: '4px solid rgba(28, 50, 79, 0.18)',
            paddingLeft: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            paddingTop: '0.5rem',
          }}>
            <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 700 }}>Enviar al paciente</h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                icon={<Icon icon='bi:chat' style={{ color: '#FFFFFF', marginRight: '10px', fontSize: '18px', flexShrink: 0 }} />}
                label={'Por WhatsApp a ' + (phone ?? '')}
                onClick={() => doSend(EMessageType.WHATSAPP)}
                disabled={disabledWhatsapp || !phone || sending}
                style={{ backgroundColor: '#14C7BC', border: 'none', color: '#FFFFFF', textAlign: 'left', justifyContent: 'flex-start', flexGrow: 1, height: 'auto', whiteSpace: 'normal', padding: '0.5rem 0.75rem' }}
              />
              <Button
                icon={<Icon icon={phone ? 'bi:pencil-square' : 'bi:plus'} style={{ color: '#01717C', fontSize: phone ? '19px' : '24px' }} />}
                onClick={handleOpenEditMobilephoneModal}
                disabled={disabledWhatsapp}
                style={{ backgroundColor: '#DBDFE5', color: '#01717C', border: 'none', borderRadius: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '35px', height: '35px', flexShrink: 0 }}
              />
            </div>

            {!loadingPerms && disabledWhatsapp && (
              <small style={{ color: '#FFB400', display: 'block', marginTop: '-5px', textAlign: 'right' }}>
                <i className='pi pi-exclamation-triangle' style={{ marginRight: '5px' }}></i>
                <label style={{ color: '#757575' }}>Servicio con costo adicional. </label>
                <a href={`https://wa.me/${customerServiceNumber}?text=${whatsappPurchaseText}`} target='_blank' rel='noreferrer' style={{ textDecoration: 'underline', color: '#01717C', fontFamily: 'sans-serif', fontStyle: 'italic' }}>Contratar ahora</a>
              </small>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                icon={<Icon icon='bi:envelope' style={{ color: '#FFFFFF', marginRight: '10px', fontSize: '18px', flexShrink: 0 }} />}
                label={'A ' + (email ?? '')}
                onClick={() => doSend(EMessageType.EMAIL)}
                disabled={disabledEmail || !email || sending}
                style={{ backgroundColor: '#14C7BC', border: 'none', color: '#FFFFFF', textAlign: 'left', justifyContent: 'flex-start', flexGrow: 1, height: 'auto', whiteSpace: 'normal', padding: '0.5rem 0.75rem' }}
              />
              <Button
                icon={<Icon icon={email ? 'bi:pencil-square' : 'bi:plus'} style={{ color: '#01717C', fontSize: email ? '19px' : '24px' }} />}
                onClick={handleOpenEditMailModal}
                style={{ backgroundColor: '#DBDFE5', color: '#01717C', border: 'none', borderRadius: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '35px', height: '35px', flexShrink: 0 }}
              />
            </div>

            <Button
              icon={<Icon icon='bi:download' style={{ color: '#3CB8BA', marginRight: '10px', fontSize: '18px' }} />}
              label='Descargar informe'
              onClick={handleDownload}
              style={{ backgroundColor: '#FFFFFF', color: '#292929', textAlign: 'left', borderColor: '#3CB8BA' }}
            />
            <Button
              icon={<Icon icon='bi:send' style={{ color: '#3CB8BA', marginRight: '10px', fontSize: '18px' }} />}
              label='Compartir por otros medios'
              onClick={handleShare}
              style={{ backgroundColor: '#FFFFFF', color: '#292929', textAlign: 'left', justifyContent: 'flex-start', borderColor: '#3CB8BA' }}
            />
            <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
              <Button
                label='Cancelar'
                onClick={onClose}
                style={{ backgroundColor: '#E8EBEF', color: '#1E1E1E', border: 'none', width: '100%' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito */}
      <Modal
        visible={sendedModal}
        setVisible={setSendedModal}
        header={
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <i className='pi pi-check' style={{ fontSize: '48px', color: '#A53E2B' }}></i>
            <label style={{ marginTop: '10px' }}><strong>¡El informe fue enviado!</strong></label>
          </div>
        }
        closable={false}
        className='h-full'
      >
        <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
          <label style={{ color: '#1E1E1E', fontSize: '14px', marginBottom: '1rem' }}>
            El paciente <strong>{details?.patientFullName}</strong> lo recibirá en:{' '}
            <strong>{viaSended === EMessageType.EMAIL ? email : phone}</strong>
          </label>
          <Button
            label='Finalizar'
            onClick={() => { setSendedModal(false); onClose() }}
            style={{ backgroundColor: '#14C7BC', border: 'none', marginBottom: '15px' }}
          />
          <Button
            label='Reenviar por otro medio'
            onClick={() => setSendedModal(false)}
            style={{ backgroundColor: '#FFFFFF', color: '#545454', border: '1px solid #3CB8BA' }}
          />
        </div>
      </Modal>

      {/* Modal editar celular */}
      <Modal
        visible={openEditMobilephoneModal}
        setVisible={setOpenEditMobilephoneModal}
        header={
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <strong>{phone ? 'Editar celular del paciente' : 'Ingresar celular del paciente'}</strong>
          </div>
        }
        headerStyle={{ fontSize: '12px' }}
        width='w-medium'
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ color: '#1E1E1E', fontSize: '14px', marginBottom: '1rem' }}>El cambio de la información se actualizará en la base de datos del paciente.</label>
          <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Celular</label>
          <InputPhone
            cboClassname='border2 w-4'
            inputClassname='border2'
            value={patientPhone}
            label='Característica sin 0 y número sin 15. (Ejemplo: 2231234567)'
            floatLabel={false}
            setValue={(mobile) => {
              setPatientPhone(mobile);
              setNewMobilephone(mobilePhoneToString(mobile));
            }}
          />
          <Button
            label={phone ? 'Cambiar' : 'Agregar'}
            onClick={() => {
              const validation = validateInternationalCellphone(newMobilephone)
              if (!validation.isValid()) {
                showMessage(toast, validation.errorMessage!, 'error')
                return
              }
              if (details) setDetails({ ...details, phonenumber: newMobilephone.replace(/[^+\d]/g, '') })
              setOpenEditMobilephoneModal(false)
            }}
            disabled={newMobilephone?.trim() === '' || /^[A-Za-z]+$/.test(newMobilephone)}
            style={{ marginTop: '8px', backgroundColor: (newMobilephone?.trim() !== '' && !/^[A-Za-z]+$/.test(newMobilephone)) ? '#14C7BC' : '#9E9E9E', border: 'none' }}
          />
        </div>
      </Modal>

      {/* Modal editar mail */}
      <Modal
        visible={openEditMailModal}
        setVisible={setOpenEditMailModal}
        header={
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <strong>{email ? 'Editar mail del paciente' : 'Ingresar mail del paciente'}</strong>
          </div>
        }
        headerStyle={{ fontSize: '12px' }}
        width='w-medium'
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ color: '#1E1E1E', fontSize: '14px', marginBottom: '1rem' }}>El cambio de la información se actualizará en la base de datos del paciente.</label>
          <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Mail</label>
          <InputTextCustom
            className='border2'
            name='email'
            value={newMail}
            onChange={(e: any) => setNewMail(e.target.value)}
          />
          <Button
            label={email ? 'Cambiar' : 'Agregar'}
            onClick={() => {
              if (!isValidEmail(newMail)) {
                showMessage(toast, 'El formato del mail no es válido.', 'error')
                return
              }
              if (details) setDetails({ ...details, email: newMail.trim() })
              setOpenEditMailModal(false)
            }}
            disabled={newMail?.trim() === ''}
            style={{ marginTop: '8px', backgroundColor: newMail?.trim() !== '' ? '#14C7BC' : '#9E9E9E', border: 'none' }}
          />
        </div>
      </Modal>

      <Toast ref={toast} position='top-right' />
    </div>
  )
}
